import { Close, Search } from '@mui/icons-material'
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import AdxButton from 'components/custom/AdxButton'
import AdxCheckbox from 'components/custom/AdxCheckbox'
import AdxFormControlLabel from 'components/custom/AdxFormControlLabel'
import AdxInput from 'components/custom/AdxInput'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import { Field, FieldArray, Form, Formik } from 'formik'
import EmployeeList from 'module/payroll/global-components/EmployeeList'
import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { employeeListYearlyReport } from 'services/api/modules/payroll/payroll-yearly-report'

const ReportEmployeeModal = ({
  isModalOpen,
  handleClose,
  modalProps,
  handleSubmission,
  isSubmitRequest,
}) => {
  const [toggleValue, setToggleValue] = useState('all')
  const [searchEmployee, setSearchEmployee] = useState('')
  const {
    titleHeader,
    descriptionHeader,
    icon,
    actionDescription, // naming for description above toggle button
    actionButton, // naming for button
  } = modalProps
  const toggleStyle = () => {
    return {
      '&.Mui-selected': {
        color: 'alternate.main',
        backgroundColor: 'alternate.light',
        border: 1,
        '&.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
          borderLeft: 1,
        },
      },
      '&:hover': {
        color: 'alternate.main',
        backgroundColor: 'gray-background.main',
      },
    }
  }

  useEffect(() => {
    setToggleValue('all')
    setSearchEmployee('')
  }, [isModalOpen])

  const { data: employees } = useQuery(['getEmployeeNamelist', modalProps.year], async () => {
    const { data } = await employeeListYearlyReport(parseInt(modalProps?.year))
    return data
  })

  return (
    <Formik
      enableReinitialize
      initialValues={{
        year: parseInt(modalProps?.year),
        form: modalProps?.form,
        staff_ids: [],
      }}
      onSubmit={handleSubmission}
    >
      {({ values, setFieldValue, handleSubmit }) => {
        return (
          <Form>
            <AdxFormModal
              open={isModalOpen}
              onClose={handleClose}
              handleClose={handleClose}
              headerBorder={false}
              childPadding={false}
              icon={icon}
              title={titleHeader}
              subtitle={descriptionHeader}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ px: 3 }}>
                  <Typography color="light-gray-text.main">{actionDescription}</Typography>
                  <ToggleButtonGroup
                    fullWidth
                    value={toggleValue}
                    onChange={(event, newValue) => {
                      setToggleValue(newValue)
                      setSearchEmployee('')
                      if (toggleValue === 'all') {
                        setFieldValue('staff_ids', [])
                      }
                    }}
                    exclusive
                    sx={{ my: 2 }}
                    disabled={isSubmitRequest}
                  >
                    <ToggleButton value="all" key="all" sx={{ ...toggleStyle() }}>
                      All employees
                    </ToggleButton>
                    <ToggleButton value="selected" key="selected" sx={{ ...toggleStyle() }}>
                      Selected employee(s)
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ px: 3 }}>
                    {toggleValue === 'selected' && (
                      <FieldArray name="staff_ids">
                        {({ form, push, remove }) => {
                          const { values } = form
                          return (
                            <AdxTabsBadge scrollButtons={false}>
                              <AdxTabsPanel label="All employees">
                                <Box>
                                  <AdxInput
                                    placeholder="Search for employee to add"
                                    onChange={(e) => setSearchEmployee(e.target.value)}
                                    fullWidth
                                    value={searchEmployee}
                                    sx={{
                                      my: 1.75,
                                      mx: 0.5,
                                      '& .MuiInputBase-root': {
                                        minHeight: '35px',
                                        outlineColor: '#E5E5E5',
                                      },
                                      '&.MuiBox-root': {
                                        bgcolor: 'white.main',
                                        width: 'auto',
                                      },
                                    }}
                                    startAdornment={
                                      <Search color="light-gray-text" sx={{ mr: 1 }} />
                                    }
                                    endAdornment={
                                      searchEmployee && (
                                        <Close
                                          color="alternate"
                                          onClick={() => setSearchEmployee('')}
                                          sx={{ cursor: 'pointer' }}
                                        />
                                      )
                                    }
                                  />
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    overflow="auto"
                                    height="calc(90vh - 432px)"
                                    sx={{}}
                                    className="scrollbar"
                                  >
                                    {employees
                                      ?.filter((item) => {
                                        if (searchEmployee === '') {
                                          return item
                                        } else if (
                                          item.name
                                            .toLowerCase()
                                            .includes(searchEmployee.toLowerCase())
                                        ) {
                                          return item
                                        } else {
                                          return null
                                        }
                                      })
                                      .map((list, index) => {
                                        return (
                                          <Field
                                            name={`staff_ids.${index}`}
                                            as={AdxFormControlLabel}
                                            onChange={(e) => {
                                              if (
                                                values.staff_ids.find(
                                                  (id) => id === parseInt(e.target.value)
                                                )
                                              ) {
                                                remove(
                                                  values.staff_ids.findIndex(
                                                    (id) => id === parseInt(e.target.value)
                                                  )
                                                )
                                              } else {
                                                push(parseInt(e.target.value))
                                              }
                                            }}
                                            type="no-fill"
                                            key={index}
                                            control={<AdxCheckbox value={list?.id} />}
                                            checked={
                                              values.staff_ids.find((item) => item === list?.id)
                                                ? true
                                                : false
                                            }
                                            label={
                                              <EmployeeList
                                                avatar={list.avatar}
                                                sxAvatar={{
                                                  width: '30px',
                                                  height: '30px',
                                                }}
                                                name={list.name}
                                                position={list.position}
                                              />
                                            }
                                          />
                                        )
                                      })}
                                  </Box>
                                </Box>
                              </AdxTabsPanel>
                              <AdxTabsPanel label={`Selected (${values.staff_ids.length})`}>
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  height="calc(90vh - 380px)"
                                  overflow="auto"
                                  sx={{}}
                                >
                                  {employees
                                    ?.filter((item) => {
                                      if (values.staff_ids.find((id) => id === item.id)) {
                                        return item
                                      } else {
                                        return null
                                      }
                                    })
                                    .map((list, index) => {
                                      return (
                                        <Field
                                          name={`staff_ids.${index}`}
                                          as={AdxFormControlLabel}
                                          type="no-fill"
                                          key={index}
                                          control={<AdxCheckbox />}
                                          checked
                                          onChange={(e) => {
                                            remove(
                                              values.staff_ids.findIndex(
                                                (id) => id === parseInt(e.target.value)
                                              )
                                            )
                                          }}
                                          label={
                                            <EmployeeList
                                              avatar={list.avatar}
                                              sxAvatar={{
                                                width: '30px',
                                                height: '30px',
                                              }}
                                              name={list.name}
                                              position={list.position}
                                            />
                                          }
                                        />
                                      )
                                    })}
                                </Box>
                              </AdxTabsPanel>
                            </AdxTabsBadge>
                          )
                        }}
                      </FieldArray>
                    )}
                  </Box>
                  <Box
                    sx={{
                      borderTop: 1,
                      borderColor: 'gray-line.main',
                      px: 3,
                      py: 2,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 1,
                    }}
                  >
                    <AdxButton variant={'outlined'} onClick={handleClose} sx={{ width: '100px' }}>
                      Cancel
                    </AdxButton>
                    {toggleValue === 'all' ? (
                      <AdxButton
                        variant={'contained'}
                        onClick={() => handleSubmit()}
                        isLoading={isSubmitRequest}
                        disabled={isSubmitRequest}
                        sx={{ width: '200px' }}
                      >{`${actionButton} (All)`}</AdxButton>
                    ) : (
                      <AdxButton
                        variant={'contained'}
                        onClick={() => handleSubmit()}
                        disabled={values.staff_ids.length === 0 || isSubmitRequest}
                        isLoading={isSubmitRequest}
                        sx={{ width: '200px' }}
                      >
                        {values.staff_ids.length === 0
                          ? `${actionButton}`
                          : `${actionButton} (${values.staff_ids.length})`}
                      </AdxButton>
                    )}
                  </Box>
                </Box>
              </Box>
            </AdxFormModal>
          </Form>
        )
      }}
    </Formik>
  )
}

export default ReportEmployeeModal
